import { useState } from 'react';
import { FileText, Loader2, ShieldAlert } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import apiV2 from '../utils/apiV2';
import { toast } from 'sonner';

const ACK_TEXT = 'I understand my business is proceeding without a liability waiver. This is my decision as the operator.';

/**
 * WaiverDecisionModal — the friendly "no waiver? totally fine, just sign off"
 * onboarding step. Records who confirmed and when (audit-trailed for super admin).
 */
export const WaiverDecisionModal = ({ open, onClose, onDecided, onSetUpWaiver }) => {
  const [acked, setAcked] = useState(false);
  const [saving, setSaving] = useState(false);

  const confirm = async () => {
    setSaving(true);
    try {
      const r = await apiV2.post('/auth/organizations/me/waiver-decision', { decision: 'no_waiver' });
      toast.success('Noted — you can add a waiver anytime from the Waivers tab');
      onDecided?.(r.data?.waiverDecision || null);
      onClose?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not save your decision');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="waiver-decision-modal">
        <div className="w-12 h-12 rounded-2xl bg-amber-50 flex items-center justify-center mb-1">
          <ShieldAlert className="w-6 h-6 text-amber-600" />
        </div>
        <DialogTitle>Running without a waiver?</DialogTitle>
        <DialogDescription className="leading-relaxed">
          We recommend collecting signed waivers — they protect your business if anything goes wrong on a trip.
          But some businesses genuinely don&apos;t need one, and that&apos;s totally fine. Just confirm it&apos;s a
          deliberate choice so we can check this off your setup.
        </DialogDescription>

        <label className="flex items-start gap-3 rounded-xl bg-gray-50 p-3.5 cursor-pointer mt-1">
          <input
            type="checkbox"
            checked={acked}
            onChange={(e) => setAcked(e.target.checked)}
            className="mt-0.5 cursor-pointer"
            data-testid="waiver-decision-ack-checkbox"
          />
          <span className="text-sm text-gray-700 leading-snug">{ACK_TEXT}</span>
        </label>

        <div className="flex flex-col sm:flex-row gap-2 mt-2">
          <Button
            variant="outline"
            className="flex-1 gap-1.5"
            onClick={() => { onClose?.(); onSetUpWaiver?.(); }}
            data-testid="waiver-decision-setup-btn"
          >
            <FileText className="w-4 h-4" /> Set up a waiver instead
          </Button>
          <Button
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            disabled={!acked || saving}
            onClick={confirm}
            data-testid="waiver-decision-confirm-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm — no waiver for us'}
          </Button>
        </div>
        <p className="text-[11px] text-gray-400 mt-1">
          Your name and the time of this confirmation are recorded. You can change your mind anytime.
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default WaiverDecisionModal;
